// Reporting — dashboard view for all (or just-mine) recurring reports.
//
// Top-level page (not buried in Admin Panel). Every active assignment
// appears as a card with: title, assignee, cadence, streak, due-state
// pill, and the latest submission body inline so the page is genuinely
// "all reports very simply and cleanly displayed" without requiring
// click-throughs to scan the workspace.
//
// Click a card → opens the existing ReportingModal (modals.jsx) for
// full submission history + week-on-week LLM summary + submit-new.
//
// Visibility:
//   admin role → sees every assignment
//   non-admin  → sees only their own (assignee_code === session.code)
//
// Assignment CRUD still lives in Admin Panel → Reporting tab; this
// page is read-focused so it loads instantly without form chrome.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { ReportingModal } from '../home/modals';
import { useToast } from '../lib/Toast';
import { usePageContext } from '../lib/PageContext';
import { humanizeError } from '../lib/humanizeError';
import { AttachmentStrip } from '../lib/AttachmentStrip';

// Admin-only assignment management. Cadence options + source hints
// mirror admin/ReportingTab.jsx (the duplicate entry point we're keeping
// for now in case Mo wants to fall back). Custom cadence is gated by
// migration 94 — it surfaces an extra "every N days" input.
const CADENCES = ['daily', 'weekly', 'fortnightly', 'monthly', 'quarterly', 'custom'];
const SOURCE_SUGGESTIONS = ['Shopify', 'Klaviyo', 'Meta + TikTok', 'Google Ads', 'GA4', 'Xero', 'Skio', 'Internal', 'Shopify + Stripe'];

// Phase 1 of Reporting v2 — fires after each submission. Lambda parses the
// body for action items + numeric metrics and writes them into
// report_action_items / report_metrics. (Case-mention extraction was
// removed 2026-05-08 alongside the Cases module.)
const EXTRACT_URL = import.meta.env.VITE_AWS_REPORT_EXTRACT_URL;

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const SANS  = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';
const MONO  = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

// Mirror of home/Home.jsx:reportDueState — keep the two in sync if you
// touch one. Tone drives the pill colour scheme.
function reportDueState(row) {
  if (!row.last_completed_at) return { tone: 'overdue', label: 'Never submitted' };
  const last = new Date(row.last_completed_at);
  const now = new Date();
  const days = Math.floor((now - last) / 86400000);
  let cadenceDays;
  if (row.cadence === 'custom') cadenceDays = row.cadence_days || 7;
  else if (row.cadence === 'daily')   cadenceDays = 1;
  else if (row.cadence === 'weekly')  cadenceDays = 7;
  else if (row.cadence === 'biweekly') cadenceDays = 14;
  else if (row.cadence === 'monthly') cadenceDays = 30;
  else cadenceDays = 7;
  const overdueBy = days - cadenceDays;
  if (overdueBy > 0)  return { tone: 'overdue', label: `${overdueBy}d overdue` };
  if (overdueBy === 0) return { tone: 'due',     label: 'Due today' };
  return { tone: 'ok', label: `In ${-overdueBy}d` };
}

function fmtRelative(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0)  return 'today';
  if (days === 1)  return 'yesterday';
  if (days < 30)   return `${days}d ago`;
  if (days < 365)  return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function cadenceLabel(row) {
  if (row.cadence === 'custom') return `every ${row.cadence_days || '?'} days`;
  return row.cadence || 'unspecified';
}

// Single hero tile.
function StatTile({ label, value, accent, sub }) {
  return (
    <div style={{
      background: '#FAFAF8', border: '1px solid #EFEDE7', borderRadius: 14,
      padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <span style={{
        fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: '#8B9094',
      }}>{label}</span>
      <span style={{
        fontFamily: SERIF, fontSize: 30, fontWeight: 400,
        color: accent || '#0F1419', letterSpacing: '-0.01em', lineHeight: 1,
      }}>{value}</span>
      {sub && (
        <span style={{
          fontFamily: SANS, fontSize: 11, color: '#6E7278', marginTop: 2,
        }}>{sub}</span>
      )}
    </div>
  );
}

export default function Reporting({ session }) {
  const isAdmin = session?.role === 'admin';
  const toast = useToast();
  const page = usePageContext();

  const [assignments, setAssignments] = useState([]);
  const [submissionsByAssignment, setSubmissionsByAssignment] = useState({}); // {id: latestRow}
  const [attachmentsBySubmission, setAttachmentsBySubmission] = useState({}); // {submission_id: [att rows]}
  const [staffByCode, setStaffByCode] = useState({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [scope, setScope] = useState(isAdmin ? 'all' : 'mine');     // admin can flip
  const [assigneeFilter, setAssigneeFilter] = useState('');         // '' | code
  const [editing, setEditing] = useState(null);                     // {row} for admin CRUD

  // Phase-1 Action Queue state.
  // actionItems holds OPEN items only (resolved/dismissed roll out of view).
  // submitterByItem maps action_item.id → the access_code that submitted the
  // parent report. Needed for non-admin's "unrouted-in-own-subs" filter; the
  // join is done client-side in two cheap queries to dodge PostgREST FK setup.
  const [actionItems, setActionItems] = useState([]);
  const [submitterByItem, setSubmitterByItem] = useState({});
  const [resolving, setResolving] = useState({});                   // {itemId: 'actioned'|'dismissed'}
  const [extractingId, setExtractingId] = useState(null);           // the submission currently being extracted

  const load = useCallback(async () => {
    setLoading(true);

    // Active assignments, optionally filtered by current user.
    let q = aws.from('report_assignments').select('*').eq('active', true);
    if (!isAdmin || scope === 'mine') q = q.eq('assignee_code', session?.code || '__none__');
    const { data: rows } = await q.order('title', { ascending: true });
    setAssignments(rows || []);

    // Latest submission per assignment — single round-trip via IN (...).
    const ids = (rows || []).map(r => r.id);
    if (ids.length > 0) {
      const { data: subs } = await aws.from('report_submissions')
        .select('*').in('assignment_id', ids).order('submitted_at', { ascending: false });
      const latest = {};
      for (const s of subs || []) {
        if (!latest[s.assignment_id]) latest[s.assignment_id] = s;
      }
      setSubmissionsByAssignment(latest);

      // Attachments for the latest submissions — one round-trip via IN.
      const latestIds = Object.values(latest).map(s => s.id);
      if (latestIds.length > 0) {
        const { data: atts } = await aws.from('report_submission_attachments')
          .select('*').in('submission_id', latestIds).order('uploaded_at', { ascending: true });
        const grouped = {};
        for (const a of atts || []) {
          (grouped[a.submission_id] ||= []).push(a);
        }
        setAttachmentsBySubmission(grouped);
      } else {
        setAttachmentsBySubmission({});
      }
    } else {
      setSubmissionsByAssignment({});
      setAttachmentsBySubmission({});
    }

    // Staff lookup so we can render assignee names rather than PINs.
    // Mig 139 (C1, 2026-05-10): staff_directory dropped the `code`
    // column; admin enumeration is now via the admin_staff_directory()
    // RPC (gated by jwt_claim('user_role') = 'admin'). Reporting is
    // admin-only so the RPC is always available here.
    const { data: staff } = await aws.rpc('admin_staff_directory');
    const byCode = {};
    for (const s of (staff || [])) {
      if (s.active) byCode[s.code] = s;
    }
    setStaffByCode(byCode);

    setLoading(false);
  }, [isAdmin, scope, session?.code]);

  // Action Queue loader — separate from `load` so the queue can refresh on
  // its own after a resolve / new submission without re-fetching every card.
  // Two cheap queries: open items, then submitter lookup. submitter is used
  // both for filtering (non-admin's unrouted-in-own-subs case) and for the
  // "from <name>" line on each row.
  const loadActionItems = useCallback(async () => {
    const { data: items } = await aws.from('report_action_items')
      .select('*')
      .eq('status', 'open')
      .order('created_at', { ascending: false })
      .limit(200);
    setActionItems(items || []);

    const subIds = [...new Set((items || []).map(i => i.submission_id))];
    if (subIds.length === 0) {
      setSubmitterByItem({});
      return;
    }
    const { data: subs } = await aws.from('report_submissions')
      .select('id, submitted_by').in('id', subIds);
    const submitterBySub = {};
    for (const s of subs || []) submitterBySub[s.id] = s.submitted_by;
    const map = {};
    for (const i of items || []) {
      map[i.id] = submitterBySub[i.submission_id] || null;
    }
    setSubmitterByItem(map);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadActionItems(); }, [loadActionItems]);

  // The user's queue:
  //   admin    → routed-to-me + every unrouted item across all reports
  //   non-admin → routed-to-me + unrouted items in MY OWN submissions
  const myActionItems = useMemo(() => {
    const me = session?.code;
    if (!me) return [];
    return actionItems.filter(it => {
      if (it.for_user_code === me) return true;
      if (it.for_user_code == null) {
        if (isAdmin) return true;
        return submitterByItem[it.id] === me;
      }
      return false;
    });
  }, [actionItems, submitterByItem, session?.code, isAdmin]);

  // Mark an item actioned / dismissed. Direct PostgREST UPDATE — the table
  // already captures resolved_by + resolved_at for audit.
  async function resolveItem(itemId, action /* 'actioned' | 'dismissed' */) {
    if (resolving[itemId]) return;
    setResolving(s => ({ ...s, [itemId]: action }));
    try {
      await aws.from('report_action_items')
        .update({
          status: action,
          resolved_at: new Date().toISOString(),
          resolved_by: session?.code || null,
        })
        .eq('id', itemId);
      // Optimistic-ish: drop from local state so the row vanishes immediately.
      setActionItems(items => items.filter(i => i.id !== itemId));
      toast.success(action === 'actioned' ? 'Marked done' : 'Dismissed');
    } catch (e) {
      const { title, body } = humanizeError(e, 'Could not update');
      toast.error(title, body);
    } finally {
      setResolving(s => {
        const next = { ...s };
        delete next[itemId];
        return next;
      });
    }
  }

  // After a submission lands, fire the report-extract Lambda. The Lambda
  // writes action items / metrics / case links inside one tx; we just
  // refresh the queue when it returns. Failure is non-blocking — the
  // submission is already saved.
  async function onAfterSubmit(submissionId) {
    if (!submissionId || !EXTRACT_URL) return;
    setExtractingId(submissionId);
    try {
      const res = await fetch(EXTRACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionId, userPin: session?.code }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const a = (data.action_items || []).length;
      const c = (data.case_links || []).length;
      const m = (data.metrics || []).length;
      if (a + c + m > 0) {
        const parts = [];
        if (a) parts.push(`${a} action item${a === 1 ? '' : 's'}`);
        if (c) parts.push(`${c} case link${c === 1 ? '' : 's'}`);
        if (m) parts.push(`${m} metric${m === 1 ? '' : 's'}`);
        toast.success('Extracted', parts.join(' · '));
      }
      loadActionItems();
    } catch (e) {
      console.warn('[report-extract] failed:', e.message);
      // Silent — the submission landed regardless.
    } finally {
      setExtractingId(null);
    }
  }

  // Apply the assignee filter (admin-only UX) on the assignments we
  // already loaded, so the dropdown is instant rather than re-fetching.
  const filtered = useMemo(() => {
    if (!assigneeFilter) return assignments;
    return assignments.filter(a => a.assignee_code === assigneeFilter);
  }, [assignments, assigneeFilter]);

  const stats = useMemo(() => {
    let overdue = 0, dueToday = 0, never = 0;
    for (const r of assignments) {
      const due = reportDueState(r);
      if (due.tone === 'overdue') overdue++;
      else if (due.tone === 'due') dueToday++;
      if (!r.last_completed_at) never++;
    }
    const totalSubs = Object.values(submissionsByAssignment).length;
    return { total: assignments.length, overdue, dueToday, never, totalSubs };
  }, [assignments, submissionsByAssignment]);

  const openReport = openId ? assignments.find(a => a.id === openId) : null;

  // Broadcast the focused report so ⌘M chat can answer "summarise this"
  // / "what's the trend on this report" without re-pasting context.
  // Cleared on close so the chat doesn't hold stale focus.
  useEffect(() => {
    if (openReport) {
      const summary = `cadence: ${cadenceLabel(openReport)}, streak: ${openReport.streak || 0}, last submitted: ${fmtRelative(openReport.last_completed_at)}, assignee: ${staffByCode[openReport.assignee_code]?.name || '—'}`;
      page.setFocus({ type: 'report_assignment', id: openReport.id, label: openReport.title, summary });
    } else {
      page.setFocus(null);
    }
    return () => page.setFocus(null);
  }, [openReport, page, staffByCode]);

  // ── Admin CRUD ────────────────────────────────────────────────────
  // Mirrors admin/ReportingTab.jsx semantics — same payload shape, same
  // authWrite path, same cadence/custom-days validation. Lives here so
  // Mo can manage assignments without leaving the dashboard.
  function openNew() {
    setEditing({
      _isNew: true, title: '', source: '', description: '',
      cadence: 'weekly', cadence_days: '', assignee_code: '', active: true,
    });
  }
  function openEdit(row) {
    // Click bubbles to the card; stop it from also opening the modal.
    setEditing({ ...row });
  }
  async function saveEditing() {
    if (!editing?.title?.trim()) {
      toast.error('Title required', 'Give the report a name (e.g. "Weekly Revenue Roll-up").');
      return;
    }
    if (!editing?.assignee_code) {
      toast.error('Assignee required', 'Pick a staff member to own this report — unowned reports never get submitted.');
      return;
    }
    let cadenceDays = null;
    if (editing.cadence === 'custom') {
      cadenceDays = parseInt(editing.cadence_days, 10);
      if (!cadenceDays || cadenceDays <= 0) {
        toast.error('Custom cadence needs a number', 'Type how many days between each report.');
        return;
      }
    }
    const payload = {
      title: editing.title.trim(),
      source: editing.source?.trim() || null,
      description: editing.description?.trim() || null,
      cadence: editing.cadence || 'weekly',
      cadence_days: cadenceDays,
      assignee_code: editing.assignee_code,
      active: editing.active !== false,
    };
    try {
      if (editing._isNew) {
        payload.created_by = session?.code || null;
        await authWrite(session, 'report_assignments', 'insert', payload);
      } else {
        await authWrite(session, 'report_assignments', 'update', payload,
          { column: 'id', value: editing.id });
      }
    } catch (e) {
      const { title, body } = humanizeError(e, editing._isNew ? 'Could not add' : 'Could not save');
      toast.error(title, body);
      return;
    }
    setEditing(null);
    load();
    toast.success(editing._isNew ? 'Report assignment added' : 'Saved');
  }
  async function deleteRow(row) {
    if (!confirm(`Delete the "${row.title}" assignment? This also removes its submission history.`)) return;
    try {
      await authWrite(session, 'report_assignments', 'delete', null,
        { column: 'id', value: row.id });
    } catch (e) {
      const { title, body } = humanizeError(e, 'Could not delete');
      toast.error(title, body);
      return;
    }
    load();
    toast.success('Deleted');
  }

  // Active staff for the assignee dropdown — system role excluded.
  const assigneeOptions = useMemo(() =>
    Object.values(staffByCode)
      .filter(u => u.role !== 'system')
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [staffByCode]);

  // Bodies of submissions can be long — clamp the inline preview to a
  // reasonable line count via -webkit-line-clamp without truncating the
  // underlying text (the modal still shows the full thing).
  const previewClamp = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  };

  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: '#F4EFE6', overflow: 'hidden',
    }}>
      {/* Header band */}
      <div style={{
        padding: '36px 48px 22px',
        borderBottom: '1px solid #E8E6E0',
        background: 'linear-gradient(180deg, #FAF8F2 0%, #F4EFE6 100%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{
              fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: '#8B9094', marginBottom: 6,
            }}>Reporting · {scope === 'all' ? 'all assignments' : 'your assignments'}</div>
          </div>

          {/* Scope + filter + Add — admin only. */}
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <div style={{ display: 'inline-flex', borderRadius: 999, background: '#fff', border: '1px solid #E8E6E0', padding: 2 }}>
                {['all', 'mine'].map(s => (
                  <button key={s} onClick={() => setScope(s)} style={{
                    padding: '6px 14px', borderRadius: 999, border: 'none', cursor: 'pointer',
                    background: scope === s ? '#0F1419' : 'transparent',
                    color: scope === s ? '#FAFAF8' : '#3E4148',
                    fontFamily: SANS, fontSize: 11.5, fontWeight: 500, textTransform: 'capitalize',
                  }}>{s === 'all' ? 'Everyone' : 'Mine'}</button>
                ))}
              </div>
              {scope === 'all' && Object.keys(staffByCode).length > 0 && (
                <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} style={{
                  padding: '7px 12px', borderRadius: 8, border: '1px solid #E8E6E0',
                  background: '#fff', fontFamily: SANS, fontSize: 12, color: '#3E4148',
                  cursor: 'pointer', appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\' viewBox=\'0 0 10 6\'%3E%3Cpath fill=\'%238B9094\' d=\'M0 0l5 6 5-6z\'/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: 26,
                }}>
                  <option value="">All assignees</option>
                  {Object.values(staffByCode)
                    .filter(s => assignments.some(a => a.assignee_code === s.code))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map(s => <option key={s.code} value={s.code}>{s.name || s.code}</option>)}
                </select>
              )}
              <button onClick={openNew} style={{
                padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: '#0F1419', color: '#FAF8F2',
                fontFamily: SANS, fontSize: 12, fontWeight: 500,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> Add report
              </button>
            </div>
          )}
        </div>

        {/* Hero tiles */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12,
          marginTop: 24,
        }}>
          <StatTile label="Total reports" value={stats.total} sub={scope === 'mine' ? 'assigned to you' : 'across the team'} />
          <StatTile label="Overdue" value={stats.overdue} accent={stats.overdue > 0 ? '#A03422' : undefined} />
          <StatTile label="Due today" value={stats.dueToday} accent={stats.dueToday > 0 ? '#B85C3C' : undefined} />
          <StatTile label="Submissions logged" value={stats.totalSubs} sub="lifetime, all reports shown" />
        </div>
      </div>

      {/* Card list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 48px 48px' }}>

        {/* Action Queue — Phase 1 of Reporting v2. Sits above the cards so
            it's the first thing Mo sees when he opens Reporting. Empty state
            is encouraging, not blank. Each row is one detected action item
            from the report-extract Lambda; click body opens the source
            report for context, the right buttons resolve the row.
            Only renders if the queue exists (no point of confusion when the
            extract Lambda hasn't wired up to a report yet). */}
        {(myActionItems.length > 0 || extractingId) && (
          <section style={{
            background: '#fff', border: '1px solid #E8E6E0', borderRadius: 14,
            padding: '20px 22px', marginBottom: 24,
          }}>
            <header style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              gap: 12, marginBottom: myActionItems.length > 0 ? 14 : 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <h2 style={{
                  fontFamily: SERIF, fontSize: 20, fontWeight: 400,
                  letterSpacing: '-0.01em', color: '#0F1419', margin: 0,
                }}>
                  {isAdmin ? 'Things needing attention' : 'Your action queue'}
                </h2>
                {myActionItems.length > 0 && (
                  <span style={{
                    padding: '2px 9px', borderRadius: 999,
                    background: '#FBEAE5', color: '#A03422',
                    fontFamily: SANS, fontSize: 11, fontWeight: 500,
                  }}>{myActionItems.length} open</span>
                )}
              </div>
              {extractingId && (
                <span style={{
                  fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: '#8B9094',
                }}>Extracting…</span>
              )}
            </header>

            {myActionItems.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {myActionItems.map((it, idx) => {
                  const assignment = assignments.find(a => a.id === it.assignment_id);
                  const submitterCode = submitterByItem[it.id];
                  const submitterName = submitterCode
                    ? (staffByCode[submitterCode]?.name || '—')
                    : 'system';
                  const isLast = idx === myActionItems.length - 1;
                  const busy = resolving[it.id];
                  return (
                    <li key={it.id} style={{
                      display: 'flex', alignItems: 'flex-start',
                      gap: 12, padding: '12px 0',
                      borderBottom: isLast ? 'none' : '1px solid #F1EFEA',
                    }}>
                      <div
                        onClick={() => assignment && setOpenId(assignment.id)}
                        style={{
                          flex: 1, minWidth: 0, cursor: assignment ? 'pointer' : 'default',
                        }}
                      >
                        <div style={{
                          fontFamily: SANS, fontSize: 13.5, color: '#0F1419',
                          lineHeight: 1.45, fontWeight: 500,
                          overflowWrap: 'anywhere',
                        }}>{it.body}</div>
                        <div style={{
                          marginTop: 4,
                          fontFamily: SANS, fontSize: 11.5, color: '#6E7278',
                        }}>
                          {assignment ? (
                            <span style={{ color: '#0F1419' }}>{assignment.title}</span>
                          ) : <span>Source report unavailable</span>}
                          <span> · from {submitterName} · {fmtRelative(it.created_at)}</span>
                          {it.source_field_label && (
                            <span style={{ color: '#8B9094' }}> · {it.source_field_label}</span>
                          )}
                          {it.for_user_code == null && (
                            <span style={{
                              marginLeft: 8,
                              padding: '1px 7px', borderRadius: 999,
                              background: '#F4EFE6', color: '#6E7278',
                              fontFamily: MONO, fontSize: 9, letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                            }}>unrouted</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          disabled={!!busy}
                          onClick={(e) => { e.stopPropagation(); resolveItem(it.id, 'actioned'); }}
                          title="Mark this done"
                          style={{
                            padding: '6px 12px', borderRadius: 8,
                            border: '1px solid #D7E5DA',
                            background: busy === 'actioned' ? '#E8EFE9' : '#F2F7F3',
                            color: '#1F4A2C', cursor: busy ? 'wait' : 'pointer',
                            fontFamily: SANS, fontSize: 11.5, fontWeight: 500,
                            opacity: busy && busy !== 'actioned' ? 0.5 : 1,
                          }}
                        >Done</button>
                        <button
                          disabled={!!busy}
                          onClick={(e) => { e.stopPropagation(); resolveItem(it.id, 'dismissed'); }}
                          title="Drop this — not actually a thing"
                          style={{
                            padding: '6px 12px', borderRadius: 8,
                            border: '1px solid #E8E6E0',
                            background: busy === 'dismissed' ? '#F4EFE6' : '#FAFAF8',
                            color: '#6E7278', cursor: busy ? 'wait' : 'pointer',
                            fontFamily: SANS, fontSize: 11.5, fontWeight: 500,
                            opacity: busy && busy !== 'dismissed' ? 0.5 : 1,
                          }}
                        >Dismiss</button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {loading ? (
          <div style={{
            padding: 60, textAlign: 'center', fontFamily: SANS, fontSize: 13, color: '#8B9094',
          }}>Loading reports…</div>
        ) : filtered.length === 0 ? (
          <div style={{
            padding: 60, textAlign: 'center', fontFamily: SANS, fontSize: 14, color: '#8B9094',
          }}>
            {assignments.length === 0
              ? (isAdmin ? '' : 'No reports assigned to you yet.')
              : 'No reports match this filter.'}
          </div>
        ) : (
          <ul style={{
            listStyle: 'none', margin: 0, padding: 0,
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16,
          }}>
            {filtered.map(r => {
              const due = reportDueState(r);
              const dueBg = due.tone === 'overdue' ? '#FBEAE5' : due.tone === 'due' ? '#FFF7E6' : '#F1EFEA';
              const dueColor = due.tone === 'overdue' ? '#A03422' : due.tone === 'due' ? '#B85C3C' : '#3E4148';
              const assignee = r.assignee_code ? (staffByCode[r.assignee_code]?.name || '—') : '—';
              const latest = submissionsByAssignment[r.id];
              return (
                <li key={r.id}
                  onClick={() => setOpenId(r.id)}
                  style={{
                    background: '#fff', border: '1px solid #E8E6E0', borderRadius: 14,
                    padding: '18px 20px', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', gap: 14,
                    transition: 'box-shadow 160ms, border-color 160ms, transform 160ms',
                    minHeight: 180,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(15,20,25,0.06)';
                    e.currentTarget.style.borderColor = '#DAD5C8';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#E8E6E0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Header row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontFamily: SERIF, fontSize: 18, fontWeight: 400,
                        letterSpacing: '-0.01em', color: '#0F1419', lineHeight: 1.25,
                      }}>{r.title}</div>
                      <div style={{
                        marginTop: 4, fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.08em',
                        textTransform: 'uppercase', color: '#6E7278',
                      }}>
                        {r.source ? `${r.source} · ` : ''}{cadenceLabel(r)}{r.streak > 0 ? ` · ▲ ${r.streak}` : ''}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 10px', borderRadius: 999,
                      fontFamily: SANS, fontSize: 11, fontWeight: 500,
                      background: dueBg, color: dueColor, flexShrink: 0,
                    }}>{due.label}</span>
                  </div>

                  {/* Meta line: assignee · last submitted */}
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center', fontFamily: SANS, fontSize: 11.5, color: '#6E7278' }}>
                    <span>
                      <span style={{ color: '#8B9094', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: MONO, fontSize: 9, marginRight: 6 }}>Assignee</span>
                      <span style={{ color: '#0F1419', fontWeight: 500 }}>{assignee}</span>
                    </span>
                    <span>
                      <span style={{ color: '#8B9094', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: MONO, fontSize: 9, marginRight: 6 }}>Last</span>
                      <span style={{ color: '#0F1419', fontWeight: 500 }}>{fmtRelative(r.last_completed_at)}</span>
                    </span>
                  </div>

                  {/* Latest submission preview */}
                  <div style={{
                    flex: 1,
                    background: '#FAFAF8', border: '1px solid #EFEDE7', borderRadius: 10,
                    padding: '12px 14px',
                    fontFamily: SANS, fontSize: 13, color: '#3E4148', lineHeight: 1.5,
                    minHeight: 70,
                    display: 'flex', flexDirection: 'column', gap: 10,
                  }}>
                    {latest ? (
                      <>
                        <span style={previewClamp}>{latest.body}</span>
                        <AttachmentStrip attachments={attachmentsBySubmission[latest.id]} />
                      </>
                    ) : (
                      <span style={{ color: '#A0A4A8', fontStyle: 'italic' }}>
                        No submissions yet — click to file the first.
                      </span>
                    )}
                  </div>

                  {/* Footer: hint + admin actions */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                  }}>
                    <span style={{
                      fontFamily: MONO, fontSize: 9, letterSpacing: '0.08em',
                      color: '#A0A4A8', textTransform: 'uppercase',
                    }}>Click to view history + AI summary →</span>
                    {isAdmin && (
                      <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                          title="Edit assignment"
                          style={{
                            padding: '4px 10px', borderRadius: 6, border: '1px solid #E8E6E0',
                            background: '#FAFAF8', color: '#3E4148', cursor: 'pointer',
                            fontFamily: SANS, fontSize: 10.5, fontWeight: 500,
                          }}
                        >Edit</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteRow(r); }}
                          title="Delete assignment + all submissions"
                          style={{
                            padding: '4px 10px', borderRadius: 6, border: '1px solid #F5DDD7',
                            background: '#FBEAE5', color: '#A03422', cursor: 'pointer',
                            fontFamily: SANS, fontSize: 10.5, fontWeight: 500,
                          }}
                        >Delete</button>
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Drill-in: existing modal handles full history + LLM summary + submit.
          onAfterSubmit fires the report-extract Lambda with the new
          submission_id; the queue refreshes when extraction returns. */}
      {openReport && (
        <ReportingModal
          report={openReport}
          session={session}
          onClose={() => setOpenId(null)}
          onSubmitted={load}
          onAfterSubmit={onAfterSubmit}
        />
      )}

      {/* Admin Add/Edit modal — portaled to body so the page's overflow
          containers can't trap it. Same field set as the AdminPanel
          tab so muscle memory carries over. */}
      {editing && createPortal(
        <>
          <div
            onClick={() => setEditing(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.4)' }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 1101, width: 540, maxWidth: '92vw', maxHeight: '88vh',
            background: '#fff', borderRadius: 16,
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <div style={{
              padding: '18px 24px', borderBottom: '1px solid #EFEDE7',
              fontFamily: SERIF, fontSize: 20, fontWeight: 400,
              letterSpacing: '-0.01em', color: '#0F1419',
            }}>{editing._isNew ? 'New report assignment' : 'Edit report assignment'}</div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 6 }}>Title</label>
                <input
                  autoFocus
                  value={editing.title}
                  onChange={e => setEditing(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Weekly Revenue Roll-up"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid #E8E6E0', fontFamily: SANS, fontSize: 13, color: '#0F1419', outline: 'none' }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 6 }}>Description (optional)</label>
                <textarea
                  rows={2}
                  value={editing.description || ''}
                  onChange={e => setEditing(f => ({ ...f, description: e.target.value }))}
                  placeholder="What should this report cover?"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid #E8E6E0', fontFamily: SANS, fontSize: 13, color: '#0F1419', outline: 'none', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 6 }}>Source</label>
                  <input
                    list="report-page-sources"
                    value={editing.source || ''}
                    onChange={e => setEditing(f => ({ ...f, source: e.target.value }))}
                    placeholder="Shopify / Klaviyo / etc"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid #E8E6E0', fontFamily: SANS, fontSize: 13, color: '#0F1419', outline: 'none' }}
                  />
                  <datalist id="report-page-sources">
                    {SOURCE_SUGGESTIONS.map(s => <option key={s} value={s} />)}
                  </datalist>
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 6 }}>Cadence</label>
                  <select
                    value={editing.cadence}
                    onChange={e => setEditing(f => ({ ...f, cadence: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid #E8E6E0', fontFamily: SANS, fontSize: 13, color: '#0F1419', outline: 'none', textTransform: 'capitalize', cursor: 'pointer' }}
                  >
                    {CADENCES.map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c}</option>)}
                  </select>
                </div>
              </div>

              {editing.cadence === 'custom' && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 6 }}>Every how many days?</label>
                  <input
                    type="number" min="1" step="1"
                    value={editing.cadence_days || ''}
                    onChange={e => setEditing(f => ({ ...f, cadence_days: e.target.value }))}
                    placeholder="e.g. 10 for every 10 days"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid #E8E6E0', fontFamily: SANS, fontSize: 13, color: '#0F1419', outline: 'none' }}
                  />
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 6 }}>
                  Assignee <span style={{ color: '#A03422' }}>*</span>
                </label>
                <select
                  required
                  value={editing.assignee_code || ''}
                  onChange={e => setEditing(f => ({ ...f, assignee_code: e.target.value }))}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8,
                    border: `1px solid ${editing.assignee_code ? '#E8E6E0' : '#F1D3CC'}`,
                    fontFamily: SANS, fontSize: 13,
                    color: editing.assignee_code ? '#0F1419' : '#A03422',
                    outline: 'none', cursor: 'pointer',
                  }}
                >
                  <option value="" disabled>— Pick someone to own this report —</option>
                  {assigneeOptions.map(u => (
                    <option key={u.code} value={u.code} style={{ color: '#0F1419' }}>{u.name || u.code}</option>
                  ))}
                </select>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: SANS, fontSize: 12.5, color: '#3E4148' }}>
                <input
                  type="checkbox"
                  checked={editing.active !== false}
                  onChange={e => setEditing(f => ({ ...f, active: e.target.checked }))}
                />
                Active (uncheck to pause without deleting)
              </label>
            </div>

            <div style={{ padding: '14px 24px', borderTop: '1px solid #EFEDE7', display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
              {!editing._isNew ? (
                <button
                  onClick={() => { const row = editing; setEditing(null); deleteRow(row); }}
                  style={{
                    padding: '8px 14px', borderRadius: 8, border: '1px solid #F5DDD7',
                    background: '#FBEAE5', color: '#A03422', cursor: 'pointer',
                    fontFamily: SANS, fontSize: 12, fontWeight: 500,
                  }}
                >Delete</button>
              ) : <span />}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setEditing(null)}
                  style={{
                    padding: '8px 14px', borderRadius: 8, border: '1px solid #E8E6E0',
                    background: '#fff', color: '#3E4148', cursor: 'pointer',
                    fontFamily: SANS, fontSize: 12, fontWeight: 500,
                  }}
                >Cancel</button>
                <button
                  onClick={saveEditing}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: '#0F1419', color: '#FAF8F2', cursor: 'pointer',
                    fontFamily: SANS, fontSize: 12, fontWeight: 500,
                  }}
                >{editing._isNew ? 'Add report' : 'Save'}</button>
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
